import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API, resolveUrl, buildSrcSet } from "@/lib/api";
import axios from "axios";

export const DEFAULT_IMAGES = {
  hero: "",
  hero_bg: "",
  kitchen: "",
  interior: "",
  exterior: "",
  painter: "",
  review_bg: "",
  complimentary: "",
  logo_circle: "",
  logo_text: "",
  logo_white: "",
  verified: "",
};

export const DEFAULT_CAROUSEL = [];

const DEFAULT_CONTENT = {
  images: {},
  carousel: [],
  review_cards: [],
  gtm: "",
  gtm_body: "",
  webhook_url: "",
  phone: "(310) 508-6563",
  captcha_enabled: true,
};

const ContentContext = createContext(null);

function injectGtm(code) {
  if (!code || window.__cs_gtm_injected) return;
  window.__cs_gtm_injected = true;
  const container = document.createElement("div");
  container.innerHTML = code;
  container.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script");
    if (old.src) s.src = old.src;
    if (old.textContent) s.text = old.textContent;
    s.async = true;
    document.head.appendChild(s);
  });
  container.querySelectorAll("noscript").forEach((n) => document.body.appendChild(n));
}

function injectGtmBody(code) {
  if (!code || window.__cs_gtm_body_injected) return;
  window.__cs_gtm_body_injected = true;
  const container = document.createElement("div");
  container.innerHTML = code;
  Array.from(container.childNodes)
    .reverse()
    .forEach((n) => document.body.insertBefore(n, document.body.firstChild));
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Cache-bust so no browser/CDN edge can ever serve a stale content payload
      // (would make replaced/deleted images keep showing the old picture on the live site).
      const { data } = await axios.get(`${API}/content?t=${Date.now()}`);
      setContent({ ...DEFAULT_CONTENT, ...data });
      injectGtm(data.gtm);
      injectGtmBody(data.gtm_body);
    } catch (e) {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const img = useCallback(
    (key) => {
      const uploaded = content.images?.[key];
      if (uploaded) return resolveUrl(uploaded);
      // undefined (not "") so React omits the src attribute instead of triggering a page re-request
      return DEFAULT_IMAGES[key] || undefined;
    },
    [content]
  );

  // Responsive srcset for a full-size uploaded image slot. Undefined for defaults.
  const imgSrcSet = useCallback(
    (key, widths = [500, 900, 1400]) => {
      const uploaded = content.images?.[key];
      return uploaded ? buildSrcSet(uploaded, widths) : undefined;
    },
    [content]
  );

  const carousel = (content.carousel && content.carousel.length ? content.carousel : DEFAULT_CAROUSEL).map(
    (c) => ({ ...c, resolved: resolveUrl(c.url, 900) || c.url, srcset: buildSrcSet(c.url, [500, 900]) })
  );

  const reviewCards = (content.review_cards || []).map((u) => ({
    src: resolveUrl(u, 900),
    srcset: buildSrcSet(u, [500, 900]),
  }));

  return (
    <ContentContext.Provider value={{ content, loading, refresh, img, imgSrcSet, carousel, reviewCards, resolve: resolveUrl }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext);
